import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2, ImageIcon } from "lucide-react";
import { api } from "@/lib/api";
import { StepShell, Field, TInput, Chips, inputCls } from "./WizardUI";
import { TONES } from "./constants";

const UploadBox = ({ label, kind, url, onUploaded, hint }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/onboarding/upload/${kind}`, fd);
      onUploaded(data.url);
      toast.success(`${label} uploaded`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <div className="rounded-lg border border-dashed border-line bg-bg-elevated/40 p-4" data-testid={`onb-upload-${kind}`}>
      <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-2">{label}</div>
      <div className="flex items-center gap-3">
        <div className="h-14 w-14 shrink-0 rounded-md border border-line bg-bg flex items-center justify-center overflow-hidden">
          {url ? <img src={url} alt={label} className="h-full w-full object-contain" />
               : <ImageIcon className="h-5 w-5 text-ink-muted" />}
        </div>
        <div className="min-w-0">
          <button type="button" onClick={() => ref.current?.click()} disabled={busy}
            data-testid={`onb-upload-${kind}-btn`}
            className="inline-flex items-center gap-1.5 rounded-md border border-line bg-bg-elevated px-3 py-1.5 text-xs text-ink-secondary hover:text-ink-primary transition disabled:opacity-50">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
            {url ? "Replace" : "Upload"}
          </button>
          {hint && <p className="text-[10px] text-ink-muted mt-1">{hint}</p>}
        </div>
      </div>
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden"
        onChange={(e) => upload(e.target.files?.[0])} />
    </div>
  );
};

const ColorPick = ({ label, value, onChange, testid }) => (
  <Field label={label}>
    <div className="flex items-center gap-2">
      <input type="color" value={value || "#FF8A1F"} onChange={(e) => onChange(e.target.value)}
        data-testid={testid} className="h-10 w-12 rounded-md border border-line bg-bg cursor-pointer" />
      <TInput value={value} onChange={(e) => onChange(e.target.value)} placeholder="#FF8A1F" />
    </div>
  </Field>
);

export default function Step3Branding({ saved, station, onSave, onBack, onSkip, busy, refresh }) {
  const s = { ...(station || {}), ...(saved || {}) };
  const [f, setF] = useState({
    brand_primary_color: s.brand_primary_color || "#FF8A1F",
    brand_secondary_color: s.brand_secondary_color || "#0093FF",
    content_tone: s.content_tone || "",
    content_tone_custom: s.content_tone_custom || "",
  });
  const [urls, setUrls] = useState({
    logo: station?.logo_url || "", square_logo: station?.square_logo_url || "", banner: station?.banner_url || "",
  });
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));

  return (
    <StepShell step={3} title="Station branding"
      subtitle="Your logo and colors flow into newsletters, social cards, and video renders."
      onBack={onBack} onContinue={() => onSave(f)} onSkip={onSkip} busy={busy}>
      <div className="grid sm:grid-cols-3 gap-3">
        <UploadBox label="Station logo" kind="logo" url={urls.logo} hint="PNG/JPG, up to 5 MB"
          onUploaded={(u) => { setUrls((x) => ({ ...x, logo: u })); refresh?.(); }} />
        <UploadBox label="Square social logo" kind="square_logo" url={urls.square_logo} hint="1:1 ratio works best"
          onUploaded={(u) => setUrls((x) => ({ ...x, square_logo: u }))} />
        <UploadBox label="Station banner" kind="banner" url={urls.banner} hint="Wide format, e.g. 1500×500"
          onUploaded={(u) => setUrls((x) => ({ ...x, banner: u }))} />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <ColorPick label="Primary brand color" value={f.brand_primary_color} onChange={set("brand_primary_color")} testid="onb-color-primary" />
        <ColorPick label="Secondary brand color" value={f.brand_secondary_color} onChange={set("brand_secondary_color")} testid="onb-color-secondary" />
      </div>
      <Field label="Preferred content tone">
        <Chips options={TONES} values={f.content_tone ? [f.content_tone] : []} single
          onChange={(v) => set("content_tone")(v[0] || "")} testidPrefix="onb-tone" />
        {f.content_tone === "Custom" && (
          <input className={`${inputCls} mt-2`} value={f.content_tone_custom} data-testid="onb-tone-custom"
            onChange={(e) => set("content_tone_custom")(e.target.value)} placeholder="Describe your tone…" />
        )}
      </Field>
      {/* Live preview */}
      <div data-testid="onb-brand-preview" className="rounded-xl border border-line overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${f.brand_primary_color || "#FF8A1F"}22, transparent 60%)` }}>
        <div className="p-5 flex items-center gap-4">
          <div className="h-14 w-14 rounded-lg border flex items-center justify-center overflow-hidden bg-bg"
            style={{ borderColor: f.brand_primary_color || "#FF8A1F" }}>
            {urls.logo ? <img src={urls.logo} alt="logo" className="h-full w-full object-contain" />
                       : <span className="font-display font-bold text-lg" style={{ color: f.brand_primary_color }}>{(station?.name || saved?.name || "S")[0]}</span>}
          </div>
          <div className="min-w-0">
            <div className="font-display font-bold text-lg text-ink-primary truncate">{station?.name || "Your Station"}</div>
            <div className="text-xs truncate" style={{ color: f.brand_secondary_color || "#0093FF" }}>
              {station?.tagline || "Your slogan appears here"}
            </div>
            {f.content_tone && <div className="font-mono text-[9px] uppercase tracking-wider text-ink-muted mt-1">Tone · {f.content_tone}</div>}
          </div>
        </div>
      </div>
    </StepShell>
  );
}
